import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import axios from 'axios';
import { IngramProduct, MappedPlanData } from './ImportSubscriptionsPlans.types';

// Constantes
const USD_TO_BRL_RATE = 5.4866;
import { getEdgeFunctionUrl } from '@/config/environment';
const CREATE_HUBSPOT_PRODUCT_URL = getEdgeFunctionUrl('create-product-hubspot');

// Funções de Normalização e Formatação

export const isUSD = (currency: string): boolean => {
  const usdVariations = ['usd', 'us$', 'u$', 'dollar', 'dólar'];
  return usdVariations.includes(currency.toLowerCase()) || currency.toLowerCase().includes('usd');
};

export const normalizeBillingFrequency = (frequency: string, forDatabase = true): string => {
  const toDbMap: Record<string, string> = {
    'monthly': 'month', 'annually': 'year', 'yearly': 'year', 'quarterly': 'quarter',
    'semiannually': 'semiannual', 'one-time': 'one-time'
  };
  const fromDbMap: Record<string, string> = {
    'month': 'monthly', 'year': 'annually', 'quarter': 'quarterly',
    'semiannual': 'semiannually', 'one-time': 'one-time'
  };
  return forDatabase ? (toDbMap[frequency] || frequency) : (fromDbMap[frequency] || frequency);
};

export const formatCurrency = (currency: string): string => {
  if (isUSD(currency)) return 'USD';
  if (currency.toLowerCase().includes('brl') || currency.includes('R$')) return 'BRL';
  return currency.substring(0, 3).toUpperCase();
};

export const normalizeBillingModel = (billingModel: string): string => {
  const modelMap: Record<string, string> = {
    'chargeBeforeBillingPeriod': 'on creation', 'chargeAfterBillingPeriod': 'on activation',
    'on_creation': 'on creation', 'on_activation': 'on activation', 'before': 'on creation',
    'after': 'on activation', 'prepaid': 'on creation', 'postpaid': 'on activation'
  };
  return modelMap[billingModel] || 'on creation';
};

export const getVendorSearchName = (vendorCode: string): string => {
  const vendorMap: Record<string, string> = {
    'VA-888-104': 'Microsoft', 'Adobe': 'Adobe', 'Google': 'Google',
    'VA-888-101': 'Oracle', 'VA-888-102': 'SAP', 'VA-888-103': 'IBM'
  };
  return vendorMap[vendorCode] || vendorCode;
};

// Funções de Interação com a API (Supabase/HubSpot)

export const getVendorId = async (vendorCode: string): Promise<string | null> => {
  const vendorName = getVendorSearchName(vendorCode);
  const queries = [
    supabase.from('vendors').select('id, name').eq('name', vendorName).maybeSingle(),
    supabase.from('vendors').select('id, name').ilike('name', `%${vendorName}%`).limit(1).maybeSingle(),
  ];
  if (vendorCode !== vendorName) {
    queries.push(supabase.from('vendors').select('id, name').ilike('name', `%${vendorCode}%`).limit(1).maybeSingle());
  }

  for (const query of queries) {
    const { data, error } = await query;
    if (error) console.error('Erro ao buscar vendor:', error);
    if (data) return data.id;
  }
  
  toast.warning(`Fornecedor "${vendorName}" não encontrado. O plano será importado sem associação.`);
  return null;
};

export const createProductInHubspot = async (plan: any, priceRecords: any[]) => {
  try {
    const getHubSpotProductType = (serviceType: string) => {
      const normalizedType = serviceType?.trim()?.toLowerCase();
      const typeMap: Record<string, string> = {
        'licensing': 'license', 'material': 'material',
        'program': 'program', 'professional_service': 'service'
      };
      return typeMap[normalizedType] || 'service';
    };

    const payload: any = {
      name: plan.name,
      hs_sku: plan.mpn_custom || plan.mpn || plan.sku,
      description: plan.description,
      recurringbillingfrequency: normalizeBillingFrequency(plan.billing_period_type, false),
      hs_product_type: getHubSpotProductType(plan.service_type || 'licensing'),
    };
    
    const brlPrice = priceRecords.find(p => p.currency === 'BRL' && p.price_amount);
    if (brlPrice) {
      payload.hs_price_brl = brlPrice.price_amount.toString();
      if (brlPrice.cost_amount) {
        payload.hs_cost_of_goods_sold = brlPrice.cost_amount.toString();
      }
    }

    if (!import.meta.env.PROD) console.log('📤 Payload para HubSpot:', JSON.stringify(payload, null, 2));

    const response = await axios.post(CREATE_HUBSPOT_PRODUCT_URL, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });
    
    return { success: true, data: response.data.data, message: 'Produto criado no HubSpot.' };
  } catch (error: any) {
    console.error('Erro ao criar produto no HubSpot:', error.response?.data || error.message);
    return { success: false, data: null, message: error.response?.data?.message || 'Erro no HubSpot.' };
  }
};

export const syncNewProductWithHubspot = async (planId: string, planData: MappedPlanData) => {
  try {
    const planForHubspot = {
      name: planData.name,
      description: planData.description,
      sku: planData.sku,
      billing_period_type: planData.billing_period_type,
      service_type: planData.service_type,
      mpn_custom: planData.mpn_custom,
      mpn: planData.vendor_part_number,
    };

    const result = await createProductInHubspot(planForHubspot, planData.prices);

    if (result.success && result.data?.id) {
      const { error: idError } = await supabase
        .from('external_ids')
        .insert({
          entity_name: 'subscription_plans',
          entity_id: planId,
          system_name: 'hubspot',
          external_id: result.data.id,
        });

      if (idError) throw new Error(`Erro ao salvar ID do HubSpot: ${idError.message}`);
      return { success: true, message: 'Sincronizado com HubSpot.' };
    } else {
      throw new Error(result.message || 'Falha ao criar/sincronizar produto no HubSpot.');
    }
  } catch (error: any) {
    console.error(`Erro ao sincronizar plano ${planId} com HubSpot:`, error);
    toast.error(`Falha na sincronização com HubSpot: ${error.message}`);
    return { success: false, message: error.message };
  }
};

// Funções de Mapeamento e Processamento de Dados

export const mapIngramProductToPlanData = (
  product: IngramProduct,
  familyId: string | null,
  vendorId: string | null
): MappedPlanData => {
  const price = parseFloat(product.prices[0]?.amount || '0');
  let cost = parseFloat(product.costs[0]?.amount || '0');
  if (cost === 0 && price > 0) {
    cost = price * 0.21054;
  }

  return {
    name: product.name,
    description: product.description || '',
    vendor: product.vendor,
    billing_frequency: normalizeBillingFrequency(product.billingPeriod?.type || 'month'),
    is_active: true,
    sku: product.mpn_custom || product.mpn,
    vendor_subscription_name: product.name,
    vendor_part_number: product.mpn,
    minimum_quantity: parseInt(product.minimumQuantity) || 1,
    maximum_quantity: parseInt(product.maximumQuantity) || 9999,
    billing_model: normalizeBillingModel(product.billingModel || 'on creation'),
    pricing_model: 'per unit',
    billing_period_type: product.billingPeriod?.type || 'month',
    billing_period_duration: product.billingPeriod?.duration || 1,
    subscription_period_type: product.subscriptionPeriod?.type || 'month',
    subscription_period_duration: product.subscriptionPeriod?.duration || 1,
    family_id: familyId,
    unit_measure: 'user',
    service_type: 'licensing',
    imported: true,
    created_by: 'import_process',
    updated_by: 'import_process',
    prices: product.prices.map(p => {
      const priceAmount = parseFloat(p.amount) || 0;
      const costAmount = product.costs.find(c => c.currency === p.currency)?.amount 
        ? parseFloat(product.costs.find(c => c.currency === p.currency)!.amount) || 0
        : priceAmount * 0.21054;
      const margin = priceAmount > 0 ? ((priceAmount - costAmount) / priceAmount) * 100 : 0;
      return {
        currency: formatCurrency(p.currency),
        price_amount: priceAmount,
        cost_amount: costAmount,
        margin: parseFloat(margin.toFixed(2)),
      };
    }),
    external_id: product.external_id,
    ingram_product_id: product.id,
    mpn_custom: product.mpn_custom,
    vendor_id: vendorId,
  };
};

export const processAndInsertPlan = async (planData: MappedPlanData): Promise<{ planId: string; finalPlanData: MappedPlanData }> => {
  const { prices, vendor, ingram_product_id, mpn_custom, external_id, ...planToInsert } = planData;
  
  const { data: planInsertData, error: planError } = await supabase
    .from('subscription_plans')
    .insert(planToInsert)
    .select('id')
    .single();

  if (planError) {
    throw new Error(`Erro ao inserir plano: ${planError.message}`, { cause: planError });
  }
  const planId = planInsertData.id;

  // Inserir ID externo da Ingram
  const { error: externalIdError } = await supabase.from('external_ids').insert({
    entity_name: 'subscription_plans',
    entity_id: planId,
    system_name: 'ingram_micro',
    external_id: mpn_custom || ingram_product_id,
  });

  if (externalIdError) {
    console.error('Erro ao inserir ID externo:', externalIdError);
    throw new Error(`Erro ao registrar ID externo: ${externalIdError.message}`);
  }

  // Inserir preços
  const pricesToInsert = prices.map(p => ({ 
    ...p, 
    subscription_plan_id: planId,
    created_by: 'import_process',
    updated_by: 'import_process',
  }));
  const { error: pricesError } = await supabase.from('subscription_plan_prices').insert(pricesToInsert);

  if (pricesError) {
    console.error('Erro ao inserir preços:', pricesError);
    throw new Error(`Erro ao inserir preços na base: ${pricesError.message}`);
  }

  const finalPrices = [...prices];

  // Converter preço USD para BRL se necessário
  const usdPrice = prices.find(p => isUSD(p.currency));
  const hasBrlPrice = prices.some(p => p.currency === 'BRL');

  if (usdPrice && !hasBrlPrice) {
    const brlPrice = {
      currency: 'BRL',
      price_amount: parseFloat((usdPrice.price_amount * USD_TO_BRL_RATE).toFixed(2)),
      cost_amount: parseFloat((usdPrice.cost_amount * USD_TO_BRL_RATE).toFixed(2)),
      margin: usdPrice.margin,
    };
    const { error: brlPriceError } = await supabase.from('subscription_plan_prices').insert({ 
      ...brlPrice, 
      subscription_plan_id: planId,
      created_by: 'import_process',
      updated_by: 'import_process',
    });

    if (brlPriceError) {
      console.error('Erro ao inserir preço BRL convertido:', brlPriceError);
      throw new Error(`Erro ao inserir preço BRL convertido: ${brlPriceError.message}`);
    }
    finalPrices.push(brlPrice);
  }

  const finalPlanData = { ...planData, prices: finalPrices };

  return { planId, finalPlanData };
};
